import { chain, forEach, isEmpty } from 'lodash';
import { format } from 'date-fns';
import i18n from 'i18next';
import {
  ARMENIAN_TIME_ZONE_HOURS,
  AUTOMATIC_RECURRING_PAYMENT_TYPES,
  CONTRACT_END_ISO_TIME,
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATE_FORMAT_WITH_TIME,
} from '../../constants';
import { PAYMENT_FREQUENCIES } from '../../constants/paymentFrequency';
import { convertUserToApiFormat, convertUserToStoreFormat } from '../contractHelpers';
import mapBackendErrorsToUserFields, { mapBackendErrorsToCalculatorFields } from '../../validations/backendErrors';
import { getArmenianLocalDate, replaceTimePortion, replaceTimeZone } from '../dates';
import { attachDefaultCityVillage } from '../countryHelper';

export const bindOncoCareContractAndSaveInLocal = (
  contract,
  dateFormat = DATE_FORMAT,
) => {
  const {
    birthday,
    citizenship,
    firstName,
    lastName,
    firstNameEn,
    lastNameEn,
    gender,
    passport,
    passportGiven,
    passportValidUntil,
    passportFrom,
    resident,
    socCard,
    contactInfo,
    memberId,
    riskAmount,
    riskLabel,
    prices,
    paymentFrequencies,
    paymentFrequency,
    draftId,
    range,
    riskAmounts,
    mobileToken,
  } = contract;

  const signer = convertUserToStoreFormat(
    {
      birthday,
      citizenship,
      firstName,
      lastName,
      firstNameEn,
      lastNameEn,
      gender,
      passport,
      passportGiven,
      passportValidUntil,
      passportFrom,
      resident,
      socCard,
      contactInfo,
      memberId,
    },
    dateFormat,
  );

  const insuredPeople = contract.insuredPeople
    && contract.insuredPeople.map((insuredPerson) => {
      const converted = convertUserToStoreFormat(insuredPerson, dateFormat);
      if (converted.memberId == null) {
        const isSigner = converted.passport === passport;
        // memberId can be 0 for signer
        converted.memberId = !isSigner ? converted.id : 0;
      }
      return converted;
    });

  const oncoCareDetails = {
    paymentFrequency: PAYMENT_FREQUENCIES[paymentFrequency],
    range: {
      start: new Date(range.start),
      end: new Date(range.end),
    },
    birthday: signer.birthday,
    prices,
    paymentFrequencies,
    riskAmount: [riskAmount],
    riskLabel,
    riskAmounts,
  };

  const hasAddedSigner = !!insuredPeople.find((insurePerson) => insurePerson.memberId === 0);
  const hasAgreedAutomaticRecurring = Object.values(
    AUTOMATIC_RECURRING_PAYMENT_TYPES,
  ).includes(oncoCareDetails.paymentFrequency.value);

  return {
    signer,
    insuredPeople,
    oncoCareDetails,
    draftId,
    hasAddedSigner,
    hasAgreedAutomaticRecurring,
    isSave: contract.isSave,
    mobileToken,
  };
};

export const convertSignedOncoCareContractToStoreFormat = (contract) => {
  const convertedContract = {
    ...contract,
    paymentFrequency: contract.paymentFrequency.code,
  };

  return bindOncoCareContractAndSaveInLocal(
    convertedContract,
    DATE_FORMAT_SHORT,
    DATE_FORMAT_WITH_TIME,
  );
};

export function mapErrorsToContractSteps(errors) {
  let signerBackendErrors;
  let calculatorBackendErrors;
  let nonInsuredPeopleErrors = [];

  // eslint-disable-next-line
  forEach(errors, (error, key) => {
    nonInsuredPeopleErrors.push(error);
  });
  nonInsuredPeopleErrors = chain(nonInsuredPeopleErrors)
    .flatten()
    .uniq()
    .value();

  signerBackendErrors = mapBackendErrorsToUserFields(nonInsuredPeopleErrors);
  calculatorBackendErrors = mapBackendErrorsToCalculatorFields(
    nonInsuredPeopleErrors,
  );

  if (isEmpty(signerBackendErrors)) {
    signerBackendErrors = null;
  }

  if (isEmpty(calculatorBackendErrors)) {
    calculatorBackendErrors = null;
  }

  return {
    signerBackendErrors,
    calculatorBackendErrors,
  };
}

export const convertOncoCareContractToApiFormat = (contract) => {
  const signer = convertUserToApiFormat(contract.signer);
  const convertedContract = { ...signer };

  const { oncoCareDetails, insuredPeople } = contract;
  const {
    range,
    paymentFrequency,
    riskAmount,
    riskLabel,
    paymentFrequencies,
    prices,
    riskAmounts,
  } = oncoCareDetails;

  convertedContract.insuredPeople = insuredPeople.map(
    (insuredPerson) => convertUserToApiFormat({
      ...insuredPerson,
      riskAmount: riskAmount[0],
      riskLabel,
    }),
  );

  const hasChild = insuredPeople.some((person) => person.isChild);

  if (hasChild) {
    // If any insured person is a child, set the signer as the parent
    convertedContract.insuredPeople[0].isParent = true;
    convertedContract.insuredPeople[0].isPerson = false;
    convertedContract.insuredPeople[0].isChild = false;
  } else {
    // If no insured person is a child, set the signer as just a person
    convertedContract.insuredPeople[0].isParent = false;
    convertedContract.insuredPeople[0].isPerson = true;
    convertedContract.insuredPeople[0].isChild = false;
  }

  convertedContract.contractStart = range.start && replaceTimeZone(
    format(range.start, DATE_FORMAT, new Date(getArmenianLocalDate())),
    ARMENIAN_TIME_ZONE_HOURS,
  );

  convertedContract.contractEnd = range.end && replaceTimePortion(replaceTimeZone(
    format((range.end), DATE_FORMAT, new Date(getArmenianLocalDate())),
    ARMENIAN_TIME_ZONE_HOURS,
  ), CONTRACT_END_ISO_TIME);

  convertedContract.paymentFrequency = paymentFrequency && paymentFrequency.value;
  convertedContract.paymentFrequencies = paymentFrequencies;
  convertedContract.prices = prices;
  // @TODO use constant here
  convertedContract.type = 'oncoCare';
  // eslint-disable-next-line prefer-destructuring
  convertedContract.riskAmount = riskAmount[0];
  convertedContract.riskAmounts = riskAmounts;
  convertedContract.riskLabel = riskLabel;
  convertedContract.range = range;
  convertedContract.isSave = contract.isSave;

  if (contract.draftId) {
    convertedContract.draftId = contract.draftId;
  }
  // When state is Yerevan, it means that cityVillage must also be Yerevan
  convertedContract.contactInfo = attachDefaultCityVillage(convertedContract.contactInfo);
  convertedContract.language = i18n.language;
  convertedContract.mobileToken = contract.mobileToken;

  return convertedContract;
};
